import React from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createCurriculum } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import { uploadImageCDN, checkValidUpload } from '../../utils/utility';
import BundledEditor from '../../components/BundledEditor';

const itemName = 'Chương trình học';
const { Text, Title } = Typography;

const CreateItem = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // categories for select (only type = 2: Danh mục khóa học) and hierarchical label + ordering
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategories'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const categoryOptions = React.useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const titleMap = all.reduce((acc, cur) => { acc[cur.id] = cur.title || ''; return acc; }, {});
    const byId = {};
    const roots = [];
    // only include type=2 in the tree
    const items = all.filter((c) => Number(c?.type) === 2);
    items.forEach((c) => { byId[c.id] = { ...c, children: [] }; });
    items.forEach((c) => {
      const pid = c.parent_id || 0;
      if (pid && byId[pid]) {
        byId[pid].children.push(byId[c.id]);
      } else {
        roots.push(byId[c.id]);
      }
    });
    // sort siblings by title asc for stable order
    const sortRecursive = (node) => {
      node.children.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      node.children.forEach(sortRecursive);
    };
    roots.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    roots.forEach(sortRecursive);
    // build label path: Root — Child — Leaf
    const res = [];
    const dfs = (node, pathTitles) => {
      const currentPath = [...pathTitles, node.title || ''];
      res.push({ value: node.id, label: currentPath.join(' — ') });
      node.children.forEach((child) => dfs(child, currentPath));
    };
    roots.forEach((r) => dfs(r, []));
    return res;
  }, [allCategoriesRes]);

  // no auto-select; keep default as placeholder "Chọn danh mục"

  // handle submit form
  const { mutate: mutateCreateItem, isPending } = useMutation({
    mutationFn: createCurriculum,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: `Thêm mới ${itemName} thành công!`,
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...',
      });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      title: data?.title,
      short_description: data?.short_description || '',
      description: data?.description,
      thumb: data?.thumb,
      category_id: Number(data?.category_id || 0),
      status: data?.status,
    };
    mutateCreateItem(dataSubmit);
  };

  const handleUploadThumb = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) {
      return false;
    }
    const url = await uploadImageCDN(file, () => {}, 'toancohien_curriculum');
    if (url) {
      notification.success({
        message: 'Tải lên thành công!',
      });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ thumb: safeUrl });
    }
    return false;
  };

  return (
    <Form
      name="create-item"
      form={form}
      layout="vertical"
      requiredMark={false}
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="title"
            label={
              <Text>
                Tên {itemName} <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: `Vui lòng nhập tên ${itemName}` }]}
          >
            <Input placeholder={`Nhập tên ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="short_description" label={<Text>Mô tả ngắn</Text>}>
            <Input.TextArea placeholder={`Nhập mô tả ngắn ${itemName}...`} autoSize={{ minRows: 2, maxRows: 5 }} maxLength={1000} showCount />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="description"
            label={<Text>Mô tả (HTML)</Text>}
            valuePropName="value"
            trigger="onEditorChange"
            getValueFromEvent={(content) => (typeof content === 'string' ? content : '')}
          >
            <BundledEditor
              init={{
                license_key: 'gpl',
                height: 400,
                menubar: false,
                automatic_uploads: true,
                file_picker_types: 'image',
                plugins: [
                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor', 'searchreplace',
                  'visualblocks', 'code', 'fullscreen', 'insertdatetime', 'media', 'table', 'help', 'wordcount'
                ],
                toolbar:
                  'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | ' +
                  'bullist numlist outdent indent | image | removeformat | code | help',
                file_picker_callback: (cb, value, meta) => {
                  const input = document.createElement('input');
                  input.setAttribute('type', 'file');
                  input.setAttribute('accept', 'image/*');
                  input.onchange = async () => {
                    const file = input.files[0];
                    if (!checkValidUpload(file)) return;
                    const url = await uploadImageCDN(file, () => {}, 'toancohien_curriculum');
                    if (url) {
                      const safeUrl = encodeURI(url);
                      cb(safeUrl, { title: file.name });
                    }
                  };
                  input.click();
                },
                images_upload_handler: async (blobInfo, progress) => {
                  const blob = blobInfo.blob();
                  if (!checkValidUpload(blob)) return '';
                  const url = await uploadImageCDN(blob, () => {}, 'toancohien_curriculum');
                  return encodeURI(url || '');
                },
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="category_id"
            label={<Text>Danh mục <span className="text-red-700">*</span></Text>}
            rules={[
              {
                validator: (_, value) => (Number(value || 0) > 0 ? Promise.resolve() : Promise.reject(new Error('Vui lòng chọn danh mục'))),
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Chọn danh mục"
              optionFilterProp="label"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={categoryOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="thumb"
            label={
              <Text>
                Ảnh đại diện
              </Text>
            }
          >
            <Input
              placeholder={`Nhập URL hoặc tải lên file...`}
              addonBefore={
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleUploadThumb}
                  className="hover:cursor-pointer"
                >
                  <UploadOutlined />
                  Upload
                </Upload>
              }
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={1}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS_CURRICULUM || AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateItem;
